/**
 * Track presets — the three named ways to walk the road, plus "Custom".
 *
 * A preset is a *complete* `FilterState`, not a patch: selecting one replaces
 * the whole state, and the active track is DERIVED by comparing the current
 * state against each preset (`deriveTrack`) rather than stored alongside it.
 * That makes the two impossible to desync — nudge any control and the track
 * becomes "custom" because the state genuinely no longer is a preset.
 */

import { DEFAULT_FILTERS, type FilterState, filtersEqual } from "./types.ts";

export type TrackPreset = "bare" | "balanced" | "completionist";
/** A preset, or "custom" once you've adjusted anything. */
export type Track = TrackPreset | "custom";

/** Patch applied over {@link DEFAULT_FILTERS} to build each preset. */
const PRESET_PATCH: Record<TrackPreset, Partial<FilterState>> = {
  // Tier 1 only, and take every shortcut on offer — the "I have two weekends" track.
  bare: { tierMax: 1, useAlternatives: true },
  // Essential + important: the ~25 entries the curation pass calls load-bearing.
  balanced: { tierMax: 2, useAlternatives: true },
  // Exactly the defaults: everything, at full runtime.
  completionist: {},
};

export interface TrackPresetDef {
  id: TrackPreset;
  label: string;
  /**
   * One line under the chip: what this track is actually promising.
   *
   * May contain `{target}`, replaced at render with the project's short title.
   * It used to name Doomsday outright, which is fine until a second guide
   * exists and its Bare Minimum chip promises only what a Marvel film assumes.
   */
  blurb: string;
}

/** Display order, narrowest first. */
export const TRACK_PRESETS: readonly TrackPresetDef[] = [
  {
    id: "bare",
    label: "Bare Minimum",
    blurb: "Only what {target} assumes you know, at the shortest route available.",
  },
  {
    id: "balanced",
    label: "Balanced",
    blurb: "The essentials plus the setup that makes them land. The recommended track.",
  },
  {
    id: "completionist",
    label: "Completionist",
    blurb: "Every stop on the road, at full runtime.",
  },
];

/** The full state a preset stands for. */
export function presetFilters(id: TrackPreset): FilterState {
  return { ...DEFAULT_FILTERS, ...PRESET_PATCH[id] };
}

/** Which track the current state IS — "custom" when it matches none of them. */
export function deriveTrack(state: FilterState): Track {
  for (const preset of TRACK_PRESETS) {
    if (filtersEqual(state, presetFilters(preset.id))) return preset.id;
  }
  return "custom";
}

const PRESET_IDS = new Set<string>(TRACK_PRESETS.map((p) => p.id));

export function isTrackPreset(value: string): value is TrackPreset {
  return PRESET_IDS.has(value);
}
